import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useAudioManager from '../../../hooks/useAudioManager'
import useWakeLock from '../../../hooks/useWakeLock'
import { hapticImpact } from '../../../utils/haptic'

const SECTOR_COUNT = 15
const SECTOR_DEG = 360 / SECTOR_COUNT
const WHEEL_START_ANGLE = -90
const DEFAULT_SPIN_DURATION_MS = 10000
// Длительность плавного доворота до центра в начале нового раунда
const ALIGN_DURATION_MS = 800

const getSpinDurationByRaffleSeconds = (raffleSeconds) => {
  const parsedRaffleSeconds = Number(raffleSeconds)
  if (!Number.isFinite(parsedRaffleSeconds) || parsedRaffleSeconds <= 0) {
    return DEFAULT_SPIN_DURATION_MS
  }

  return Math.max(0, Math.round(parsedRaffleSeconds * 1000) - 400)
}

// Фиксированное количество полных оборотов при вращении
const FULL_TURNS = 4

const getSectorColor = (sector) => {
  if (sector === 0) return 'green'
  return sector % 2 === 0 ? 'red' : 'black'
}

const getSectorRotation = (sector) => {
  const normalized = Number.isFinite(Number(sector)) ? Number(sector) : 0
  const centerAngle = WHEEL_START_ANGLE + normalized * SECTOR_DEG + SECTOR_DEG / 2
  return -centerAngle
}

// Рандомный сдвиг внутри сектора: от -40% до +40% ширины сектора (не ближе 10% к краям)
const getRandomOffsetInSector = () => {
  return (Math.random() - 0.5) * 0.8 * SECTOR_DEG
}

// Rotation до рандомной точки внутри сектора
const getSectorRotationWithOffset = (sector) => {
  const base = getSectorRotation(sector)
  return base + getRandomOffsetInSector()
}

const buildGradient = () => {
  const colorMap = {
    red: '#ef4444',
    black: '#111827',
    green: '#22c55e'
  }

  const stops = []
  for (let i = 0; i < SECTOR_COUNT; i += 1) {
    const color = colorMap[getSectorColor(i)]
    const start = i * SECTOR_DEG
    const end = start + SECTOR_DEG
    stops.push(`${color} ${start}deg ${end}deg`)
  }

  return `conic-gradient(from ${WHEEL_START_ANGLE}deg, ${stops.join(', ')})`
}

const formatTimer = (millisecondsValue) => {
  const safeMilliseconds = Math.max(0, Number(millisecondsValue || 0))
  const totalTenths = Math.floor(safeMilliseconds / 100)
  const seconds = Math.floor(totalTenths / 10)
  const tenths = totalTenths % 10
  return `${seconds}.${tenths}`
}

const DoubleWheel = ({
  roundId,
  status,
  prevSector,
  winSector,
  winColor,
  countdownMs,
  raffleSeconds,
  raffleDurationSeconds,
  raffleEndTimeMs,
  pinResultToCenter = false,
  showResultSectorOnly = false
}) => {
  const { t } = useTranslation()
  const { playSound, stopSound } = useAudioManager({
    spinReel: 'doubleSpinReel',
    timerStartEnd: 'doubleTimerStartEnd'
  })

  const [rotation, setRotation] = useState(() => getSectorRotation(prevSector))
  const [spinDurationMs, setSpinDurationMs] = useState(DEFAULT_SPIN_DURATION_MS)
  const [spinFromRotation, setSpinFromRotation] = useState(0)
  const [spinElapsedMs, setSpinElapsedMs] = useState(0)
  const [isAnimating, setIsAnimating] = useState(false)

  // Удержание экрана включённым во время вращения колеса
  useWakeLock(isAnimating)
  // Флаг плавного доворота до центра в начале нового раунда
  const [isAligning, setIsAligning] = useState(false)
  const [showNewStatusLabel, setShowNewStatusLabel] = useState(
    status === 'new' && Number(countdownMs || 0) >= 7000
  )
  const animatedRoundRef = useRef(null)
  const rotationRef = useRef(getSectorRotation(prevSector))
  const statusRef = useRef(status)
  const winSectorRef = useRef(winSector)
  const hasPlayedTimerForRoundRef = useRef({ roundId: null, started: false })
  const spinPlayingRef = useRef(false)
  // Флаг: анимация raffle была запущена для текущего раунда (защита от мерцания центра)
  const spinStartedRef = useRef(false)
  // Последняя зафиксированная целая секунда countdown — для виброотклика на тиках 3/2/1
  const lastCountdownSecondRef = useRef(null)

  const sectorGradient = useMemo(() => buildGradient(), [])
  const displaySector = Number.isFinite(Number(winSector))
    ? Number(winSector)
    : Number.isFinite(Number(prevSector))
      ? Number(prevSector)
      : 0

  const statusLabel = useMemo(() => {
    const statusMap = {
      new: t('double.status.new', 'Делайте ставки'),
      started: t('double.status.started', 'Ставки открыты'),
      raffle: t('double.status.raffle', 'Розыгрыш'),
      result: t('double.status.result', 'Результат'),
      finished: t('double.status.finished', 'Делайте ставки')
    }

    return status ? statusMap[status] || status : null
  }, [status, t])

  // Колесо стоит на секторе, если розыгрыш уже завершился, а статус ещё не обновился
  const raffleLanded = status === 'raffle' && !isAnimating && !spinStartedRef.current
  const isResultStatus = status === 'result' || raffleLanded
  const isCenterResultMode = isResultStatus || showResultSectorOnly
  const isStartedStatus = status === 'started'
  const isNewStatus = status === 'new'
  const isTimerVisible = !showResultSectorOnly && (isStartedStatus || (isNewStatus && !showNewStatusLabel))
  const isRaffleStatus = status === 'raffle' && !raffleLanded
  const isFinishedStatus = ['result', 'finished'].includes(status) || raffleLanded
  const showGlow = ['new', 'started', 'raffle', 'result', 'finished'].includes(status)
  // Золотое/огненное свечение при кручении и результате
  const showGoldGlow = isRaffleStatus || isFinishedStatus
  const greenGlowSpinClass = 'bg-[conic-gradient(from_0deg,rgba(16,185,129,0.12),rgba(34,211,238,0.48),rgba(16,185,129,0.12),rgba(34,211,238,0.54),rgba(16,185,129,0.12))]'
  const greenGlowPulseClass = 'bg-[radial-gradient(circle,rgba(34,211,238,0.5)_0%,rgba(16,185,129,0.24)_38%,rgba(16,185,129,0)_72%)]'
  const centerStyle = useMemo(() => {
    if (!isCenterResultMode) {
      return { backgroundColor: 'rgba(10, 10, 15, 0.95)', color: '#ffffff' }
    }

    const normalizedColor = String(winColor || getSectorColor(displaySector)).toLowerCase()

    if (normalizedColor === 'red') {
      return { backgroundColor: '#ef4444', color: '#ffffff' }
    }

    if (normalizedColor === 'black') {
      return { backgroundColor: '#111827', color: '#ffffff' }
    }

    return { backgroundColor: '#22c55e', color: '#052e16' }
  }, [displaySector, isCenterResultMode, winColor])

  useEffect(() => {
    rotationRef.current = rotation
  }, [rotation])

  useEffect(() => {
    statusRef.current = status
    winSectorRef.current = winSector
  }, [status, winSector])

  useEffect(() => {
    const isBettingStatus = ['new', 'started'].includes(status)

    if (!roundId) {
      stopSound('timerStartEnd')
      hasPlayedTimerForRoundRef.current = { roundId: null, started: false }
      return
    }

    if (isBettingStatus) {
      const timerState = hasPlayedTimerForRoundRef.current
      const isNewRound = timerState.roundId !== roundId

      if (isNewRound) {
        hasPlayedTimerForRoundRef.current = { roundId, started: true }
        playSound('timerStartEnd', null, { loop: true })
        return
      }

      if (!timerState.started) {
        hasPlayedTimerForRoundRef.current = { roundId, started: true }
        playSound('timerStartEnd', null, { loop: true })
      }

      return
    }

    stopSound('timerStartEnd')
    hasPlayedTimerForRoundRef.current = { roundId, started: false }
  }, [playSound, roundId, status, stopSound])

  useEffect(() => {
    return () => {
      stopSound('timerStartEnd')
      stopSound('spinReel')
      spinPlayingRef.current = false
    }
  }, [stopSound])

  useEffect(() => {
    if (!roundId) return

    const currentStatus = statusRef.current
    const currentWinSector = winSectorRef.current

    if (currentStatus === 'raffle' && currentWinSector != null && Number.isFinite(Number(currentWinSector))) {
      animatedRoundRef.current = null
      spinStartedRef.current = false
      setIsAnimating(false)
      setIsAligning(false)
      const startRotation = getSectorRotation(prevSector)
      rotationRef.current = startRotation
      setRotation(startRotation)
      return
    }

    animatedRoundRef.current = null
    spinStartedRef.current = false
    setIsAnimating(false)

    // Плавно докручиваем от текущей (рандомной) позиции до центра prevSector
    const targetCenterRotation = getSectorRotation(prevSector)
    const currentRotation = Number(rotationRef.current || 0)
    // Находим ближайший эквивалент targetCenterRotation к текущему углу
    const diff = targetCenterRotation - currentRotation
    const normalizedDiff = ((diff % 360) + 540) % 360 - 180
    const alignTarget = currentRotation + normalizedDiff

    // Если разница существенна — плавно доворачиваем
    if (Math.abs(normalizedDiff) > 0.5) {
      setIsAligning(true)
      setSpinDurationMs(ALIGN_DURATION_MS)
      requestAnimationFrame(() => {
        rotationRef.current = alignTarget
        setRotation(alignTarget)
      })
    } else {
      setIsAligning(false)
      setSpinDurationMs(DEFAULT_SPIN_DURATION_MS)
      rotationRef.current = targetCenterRotation
      setRotation(targetCenterRotation)
    }
  }, [prevSector, roundId])

  useEffect(() => {
    if (!roundId) return

    if (!['result', 'finished'].includes(status)) return
    if (!Number.isFinite(Number(winSector))) return

    // Если raffle-анимация уже отыграла для этого раунда —
    // колесо уже стоит на рандомной позиции внутри сектора, не перетираем.
    if (!pinResultToCenter && animatedRoundRef.current === roundId) return

    const syncedRotation = pinResultToCenter
      ? getSectorRotation(winSector)
      : getSectorRotationWithOffset(winSector)

    spinStartedRef.current = false
    setIsAnimating(false)
    setIsAligning(false)
    setSpinDurationMs(DEFAULT_SPIN_DURATION_MS)
    rotationRef.current = syncedRotation
    setRotation(syncedRotation)
  }, [pinResultToCenter, roundId, status, winSector])

  const hasTimeForNewStatus = Number(countdownMs || 0) >= 7000
  useEffect(() => {
    if (status !== 'new') {
      setShowNewStatusLabel(false)
      return undefined
    }

    if (!hasTimeForNewStatus) {
      setShowNewStatusLabel(false)
      return undefined
    }

    setShowNewStatusLabel(true)
    const timeoutId = setTimeout(() => {
      setShowNewStatusLabel(false)
    }, 2000)

    return () => clearTimeout(timeoutId)
  }, [hasTimeForNewStatus, roundId, status])

  // Тактильные «тики» обратного отсчёта на последних секундах (3 → 2 → 1)
  useEffect(() => {
    if (!['new', 'started'].includes(status)) {
      lastCountdownSecondRef.current = null
      return
    }

    const remainingMs = Number(countdownMs || 0)
    if (!Number.isFinite(remainingMs) || remainingMs <= 0) return

    const currentSecond = Math.ceil(remainingMs / 1000)
    const previousSecond = lastCountdownSecondRef.current

    if (previousSecond === currentSecond) return
    lastCountdownSecondRef.current = currentSecond

    // Вибрируем только при реальном уменьшении (3 → 2 → 1), не на первом заходе
    if (
      previousSecond !== null
      && currentSecond < previousSecond
      && currentSecond >= 1
      && currentSecond <= 3
    ) {
      hapticImpact('light')
    }
  }, [countdownMs, status])

  useEffect(() => {
    if (!roundId || winSector == null || !Number.isFinite(Number(winSector))) return

    if (status === 'raffle') {
      if (animatedRoundRef.current === roundId) return

      animatedRoundRef.current = roundId

      const nextSpinDuration = getSpinDurationByRaffleSeconds(raffleDurationSeconds ?? (raffleEndTimeMs == null ? raffleSeconds : DEFAULT_SPIN_DURATION_MS / 1000))
      const endTime = Number(raffleEndTimeMs)
      const remainingMs = raffleEndTimeMs != null && Number.isFinite(endTime) ? endTime - Date.now() - 400 : nextSpinDuration
      const currentRotation = Number(rotationRef.current || 0)
      // Останавливаемся в рандомном месте выигрышного сектора
      const targetBaseRotation = getSectorRotationWithOffset(winSector)
      const minTargetRotation = currentRotation - FULL_TURNS * 360
      const turnsOffset = Math.ceil((targetBaseRotation - minTargetRotation) / 360)
      const targetRotation = targetBaseRotation - 360 * turnsOffset

      if (remainingMs <= 0 || nextSpinDuration <= 0) {
        spinStartedRef.current = false
        rotationRef.current = targetRotation
        setRotation(targetRotation)
        return
      }

      spinStartedRef.current = true
      setIsAligning(false)
      setSpinFromRotation(currentRotation)
      setSpinElapsedMs(Math.max(0, nextSpinDuration - remainingMs))
      setSpinDurationMs(nextSpinDuration)
      setRotation(targetRotation)
      setIsAnimating(true)
      stopSound('timerStartEnd')
      // Тактильный «толчок» в момент старта вращения колеса
      if (nextSpinDuration - remainingMs < 500) hapticImpact('medium')
      if (!spinPlayingRef.current) {
        playSound('spinReel', null, { loop: true })
        spinPlayingRef.current = true
      }
      return
    }

    // При result/finished — оставляем колесо на текущей позиции (не дёргаем на центр)
    if (['result', 'finished'].includes(status)) {
      stopSound('spinReel')
      spinPlayingRef.current = false
      setIsAnimating(false)
      setIsAligning(false)
    }
  }, [playSound, raffleDurationSeconds, raffleEndTimeMs, raffleSeconds, roundId, status, stopSound, winSector])

  const labels = Array.from({ length: SECTOR_COUNT }, (_, sector) => {
    const centerConicAngle = WHEEL_START_ANGLE + sector * SECTOR_DEG + SECTOR_DEG / 2
    const angle = centerConicAngle - 90
    const radius = 39
    const x = 50 + radius * Math.cos((angle * Math.PI) / 180)
    const y = 50 + radius * Math.sin((angle * Math.PI) / 180)
    const radialAngle = (Math.atan2(y - 50, x - 50) * 180) / Math.PI

    return {
      sector,
      x,
      y,
      rotation: radialAngle + 90,
      color: getSectorColor(sector)
    }
  })

  return (
    <div className="p-4 lg:p-6">

      <div className="relative mx-auto aspect-square w-full max-w-[340px]">
        <div
          className={`pointer-events-none absolute -inset-[5%] z-0 rounded-full transition-opacity duration-1500 ease-out ${showGlow ? 'opacity-100' : 'opacity-0'}`}
        >
          {/* Золотое/огненное свечение при кручении и результате */}
          <div
            className={`absolute inset-0 rounded-full blur-xl animate-[spin_4.8s_linear_infinite] bg-[conic-gradient(from_0deg,rgba(251,191,36,0.12),rgba(245,158,11,0.55),rgba(251,191,36,0.12),rgba(234,88,12,0.5),rgba(251,191,36,0.12))] transition-opacity duration-700 ease-out ${showGoldGlow ? 'opacity-100' : 'opacity-0'}`}
          />
          <div
            className={`absolute inset-3 rounded-full blur-2xl animate-[pulse_2.4s_ease-in-out_infinite] bg-[radial-gradient(circle,rgba(251,191,36,0.5)_0%,rgba(245,158,11,0.28)_38%,rgba(234,88,12,0)_72%)] transition-opacity duration-700 ease-out ${showGoldGlow ? 'opacity-100' : 'opacity-0'}`}
          />
          {/* Зелёное свечение при ожидании */}
          <div
            className={`absolute inset-0 rounded-full blur-xl animate-[spin_4.8s_linear_infinite] ${greenGlowSpinClass} transition-opacity duration-700 ease-out ${!showGoldGlow ? 'opacity-100' : 'opacity-0'}`}
          />
          <div
            className={`absolute inset-3 rounded-full blur-2xl animate-[pulse_2.4s_ease-in-out_infinite] ${greenGlowPulseClass} transition-opacity duration-700 ease-out ${!showGoldGlow ? 'opacity-100' : 'opacity-0'}`}
          />
        </div>

        <div className="absolute left-1/2 top-0 z-20 -translate-x-1/2">
          <div className="h-0 w-0 border-l-[12px] border-r-[12px] border-b-[20px] border-l-transparent border-r-transparent border-b-white drop-shadow-[0_0_10px_rgba(255,255,255,0.6)]" />
        </div>

        <div
          className="absolute inset-0 rounded-full shadow-[0_0_40px_rgba(0,0,0,0.35)]"
          style={{
            background: sectorGradient,
            transform: `rotate(${rotation}deg)`,
            '--spin-from': `${spinFromRotation}deg`,
            '--spin-to': `${rotation}deg`,
            animation: isAnimating ? `wheel-raffle-spin ${spinDurationMs}ms cubic-bezier(0.1, 0.25, 0.25, 1) -${spinElapsedMs}ms both` : 'none',
            transition: isAligning ? `transform ${ALIGN_DURATION_MS}ms cubic-bezier(0.25, 0.1, 0.25, 1)` : 'none'
          }}
          onAnimationEnd={(event) => {
            // Колесо приземлилось после раффла — тяжёлый «удар»;
            // плавный доворот (align) в начале раунда не вибрирует.
            if (event.animationName !== 'wheel-raffle-spin') return
            hapticImpact('heavy')
            setIsAnimating(false)
          }}
          onTransitionEnd={(event) => {
            if (event.propertyName === 'transform') setIsAligning(false)
          }}
        >
          <svg viewBox="0 0 100 100" className="h-full w-full">
            {labels.map((label) => (
              <text
                key={label.sector}
                x={label.x}
                y={label.y}
                textAnchor="middle"
                dominantBaseline="middle"
                transform={`rotate(${label.rotation} ${label.x} ${label.y})`}
                fill={label.color === 'green' ? '#d1fae5' : '#ffffff'}
                fontSize="6"
                fontWeight="700"
              >
                {label.sector}
              </text>
            ))}
          </svg>
        </div>

        <div
          className="absolute inset-[25%] z-10 rounded-full flex items-center justify-center transition-colors duration-300"
          style={centerStyle}
        >
          <div className="relative h-9 w-full px-1.5">
            <span
              className={`absolute inset-0 flex items-center justify-center text-center leading-tight font-bold transition-opacity duration-500 ${isCenterResultMode ? 'text-xl' : 'text-sm'} ${isCenterResultMode || !isTimerVisible ? 'opacity-100' : 'opacity-0'}`}
            >
              {isCenterResultMode ? displaySector : statusLabel}
            </span>
            <span
              className={`absolute inset-0 flex items-center justify-center text-center leading-tight font-bold font-mono text-3xl lg:text-4xl transition-opacity duration-500 ${isTimerVisible ? 'opacity-100' : 'opacity-0'}`}
            >
              {formatTimer(countdownMs)}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DoubleWheel
